<template>
    <div class="flex flex-col overflow-hidden">
        <div class="flex-none border-b py-1">
            <div class="inline-flex items-center gap-2">
                <span class="text-2xl bold">
                    {{ journey.code }}
                </span>
                <hts-icons
                    v-if="journey.processAlertStatus > 30"
                    svg="warning-sign"
                    class="inline-block"
                    color="red" />
                <hts-icons
                    v-if="isValidWaitingTime(journey, 'totalWaitingTime')"
                    svg="hourglass"
                    fill-color="orange"
                    class="inline-block" />
                <hts-icons
                    v-if="hasDriverAlert"
                    svg="driver"
                    color="purple"
                    class="inline-block" />
            </div>
            <div
                v-if="!journeyEnd"
                class="right float-right">
                <template v-if="hasVehicle">
                    <hts-form-button
                        v-if="editable && !isPartnerJourney"
                        size="v-mini"
                        color="warning"
                        icon-name="hi-arrows-right-left-icon"
                        @click="openChangeVehicle">
                        Cắt Mooc
                    </hts-form-button>
                    <hts-form-button
                        v-if="editable && !isPartnerJourney"
                        size="v-mini"
                        color="warning"
                        icon-name="hi-arrow-path-rounded-square-icon"
                        @click="openChangeVehicleAndMooc">
                        Đổi xe
                    </hts-form-button>
                    <hts-form-button
                        v-if="editable && timeEditable"
                        size="v-mini"
                        color="primary"
                        icon-name="hi-clock-icon"
                        @click="openTiming">
                        Nhập thời gian
                    </hts-form-button>
                </template>
                <hts-form-button
                    v-if="editable && !isPartnerJourney"
                    size="v-mini"
                    color="info"
                    icon-name="hi-calendar-icon"
                    @click="openPlanning">
                    {{ $t('labels.planningModify') }}
                </hts-form-button>
            </div>
        </div>
        <div class="h-10 py-1">
            <hts-form-toggle
                v-model="expanded"
                size="v-mini"
                color="primary"
                class="inline-block"
                label="Tác vụ: Expand/Collapse" />
            <hts-form-toggle
                v-model="showTrip"
                size="v-mini"
                color="primary"
                class="inline-block"
                label="Hiển thị chuyến" />
        </div>
        <hts-travel-details
            ref="travelRoute"
            :journey-id="journey._id"
            :partner-id="journey.partnerId"
            :travel-details="travelDetails"
            :travel-node-data-arr="travelNodeDataArr"
            :stage-start="journey.stageIndexBegin"
            :stage-end="journey.stageIndexEnd"
            :start="journey.travelIndexStart"
            :end="journey.travelIndexEnd"
            :show-trip="showTrip"
            :start-at="journey.startAt"
            :start-at-estimate="journey.startAtEstimate"
            :start-at-actual-estimate="journey.startAtEstimateEstimate"
            :start-at-actual="journey.startAtActual"
            :start-at-parking="journey.startAtParking"
            :start-at-location="journey.startAtLocation"
            :stop-at="journey.stopAt"
            :stop-at-estimate="journey.stopAtEstimate"
            :stop-at-actual-estimate="journey.stopAtActualEstimate"
            :stop-at-actual="journey.stopAtActual"
            :stop-at-parking="journey.stopAtParking"
            :stop-at-location="journey.stopAtLocation"
            :travel-index="journey.travelIndex"
            :travel-stage-index="journey.travelStageIndex"
            :process-alert-status="journey.processAlertStatus"
            direction="horizontal"
            class="flex-1 overflow-auto"
            @clickStep="clickStep" />
    </div>
</template>

<script setup lang="ts">
import { isValidWaitingTime } from '@/components/travel-details/-utils/timing'
const emits = defineEmits(['modify', 'update-route', 'open-timing', 'open-planning', 'open-change-vehicle', 'open-change-vehicle-and-mooc', 'show-trip'])
const localTravelDetails = ref<{ [key: string]: any }[]>([])
const localPositionsAndWaitingTasks = ref<{ [key: string]: any }[]>([])
const travelRoute = ref<any>(null)
const props = defineProps({
  journey: {
    type: Object,
    required: true
  },
  editable: Boolean
})

const { journey } = toRefs(props)
const travelDetails = computed(() => journey.value?.travelDetails)
const travelNodeDataArr = computed(() => journey.value?.travelNodeDataArr || [])
const positionsAndWaitingTasks = computed(() => journey.value?.positionsAndWaitingTasks)
const isPartnerJourney = computed(() => !!journey.value?.partnerId)
const expanded = ref(false)
const showTrip = ref(false)
const hasVehicle = computed(() => !!journey.value?.vehicle || !!journey.value?.vehicleTemp)
const journeyEnd = computed(() => journey.value?.status === 6)
const timeEditable = computed(() => !!journey.value?.timeEditable)
const hasDriverAlert = computed(() => {
  return !!journey.value?.hasDriverAlert
})

onMounted(() => {
  localTravelDetails.value = travelDetails?.value || []
  localPositionsAndWaitingTasks.value = positionsAndWaitingTasks?.value || []
})

const show = (journey: any) => {
  localTravelDetails.value = journey.travelDetails
  localPositionsAndWaitingTasks.value = journey.positionsAndWaitingTasks
}

const close = () => {
  localTravelDetails.value = []
  localPositionsAndWaitingTasks.value = []
}

const nodeShowTasks = ref([] as string[])

const clickStep = (value: any) => {
  if (!value?._id) {
    return
  }

  if (nodeShowTasks.value.includes(value._id)) {
    nodeShowTasks.value = nodeShowTasks.value.filter((id) => id !== value._id)
    return
  }
  nodeShowTasks.value.push(value._id)
}
watch(
  () => expanded.value,
  (val: boolean) => {
    toggleExpandCollapse(val)
  }
)
watch(
  () => showTrip.value,
  (val: boolean) => {
    emits('show-trip', val)
  }
)
const toggleExpandCollapse = $lodash.debounce((val: boolean) => {
  if (!travelRoute.value) {
    return
  }
  travelRoute.value.toggleExpandCollapse(val)
}, 300)

const openPlanning = (e: any) => {
  emits('open-planning', e)
}
const openTiming = (e: any) => {
  emits('open-timing', e)
}
const openChangeVehicle = (e: any) => {
  emits('open-change-vehicle', e)
}
const openChangeVehicleAndMooc = (e: any) => {
  emits('open-change-vehicle-and-mooc', e)
}

defineExpose({
  show,
  close
})
</script>
